/**
 * Facade for the Organization API.
 * Backed by the real backend (bimiq-backend-python) — no mock layer.
 */

import { ApiFacade } from './apiFacade';
import { AdminInvitation, Organization, OrgMember, OrganizationMemberRole, OrgRole } from '../../@types/organization';
import { API_BASE_URL } from '../../apiConfig';

const ORGANIZATIONS_URL = `${API_BASE_URL}/api/v1/organizations`;
const ORGANIZATION_MEMBERS_URL = `${ORGANIZATIONS_URL}/members`;
const ORGANIZATION_INVITATIONS_URL = `${ORGANIZATIONS_URL}/invitations`;

export async function getMyOrganization(accessToken: string): Promise<Organization> {
    return ApiFacade.getJson<Organization>(ORGANIZATIONS_URL, { accessToken });
}

export async function createOrganization(accessToken: string, name: string): Promise<Organization> {
    return ApiFacade.postJson<Organization>(ORGANIZATIONS_URL, {
        accessToken,
        body: { name },
    });
}

export async function renameOrganization(accessToken: string, name: string): Promise<Organization> {
    return ApiFacade.patchJson<Organization>(ORGANIZATIONS_URL, {
        accessToken,
        body: { name },
    });
}

export async function getOrganizationMembers(accessToken: string): Promise<OrgMember[]> {
    const response = await ApiFacade.getJson<{ members: OrgMember[] }>(ORGANIZATION_MEMBERS_URL, { accessToken });
    return response.members;
}

export async function updateOrganizationMemberRole(
    accessToken: string,
    userObjectId: string,
    role: OrganizationMemberRole
): Promise<OrgMember> {
    return ApiFacade.patchJson<OrgMember>(`${ORGANIZATION_MEMBERS_URL}/${userObjectId}`, {
        accessToken,
        body: { role },
    });
}

export async function removeOrganizationMember(accessToken: string, userObjectId: string): Promise<void> {
    await ApiFacade.deleteJson(`${ORGANIZATION_MEMBERS_URL}/${userObjectId}`, { accessToken });
}

export async function getOrganizationInvitations(accessToken: string): Promise<AdminInvitation[]> {
    const response = await ApiFacade.getJson<AdminInvitation[] | { invitations: AdminInvitation[] }>(
        ORGANIZATION_INVITATIONS_URL,
        { accessToken }
    );

    // The organization API groups collection responses under a named field
    // (as it does for members). Accept a bare list too so an older deployment
    // cannot crash the management page during a rollout.
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.invitations)) return response.invitations;
    throw new Error('Invalid invitation list response.');
}

export async function createOrganizationInvitation(
    accessToken: string,
    email: string,
    role: OrgRole
): Promise<AdminInvitation> {
    return ApiFacade.postJson<AdminInvitation>(ORGANIZATION_INVITATIONS_URL, {
        accessToken,
        body: { email, role },
    });
}

export async function revokeOrganizationInvitation(accessToken: string, invitationObjectId: string): Promise<void> {
    await ApiFacade.deleteJson(`${ORGANIZATION_INVITATIONS_URL}/${invitationObjectId}`, { accessToken });
}
